import type { AgentPlatform } from "./AgentEstateService";

// ─────────────────────────────────────────────────────────────────────────────
// Governance Overview — the contracts the Security Overview's two tabs need and
// the backend does not have yet. Mock-only: inside `<MockScope>` the mock
// registry serves them from `mock-data/trustai-executive/governanceFixtures`.
//
// Live mode reads the Data Sources tab from real APIs instead (accounts, alert
// counts, monitoring policies) and keeps the AI Agents tab disabled. The gaps
// these stand in for:
//  - access-control deployment state aggregated per tenant (deployed / saved /
//    failed),
//  - agent protection aggregated by business unit, department or geography ×
//    platform, with explicit platform connection metadata per group,
//  - agent runtime activity counts for a time window.
// ─────────────────────────────────────────────────────────────────────────────

/** Development-only review states, selected with `?dsScenario=` / `?aiScenario=`. */
export type DataGovernanceScenario = "attention" | "healthy" | "empty";
export type AgentGovernanceScenario =
  | "attention"
  | "healthy"
  | "quiet"
  | "empty";

/** Same shape as the board's `PlatformAlertCategory`, so rows need no remap. */
export interface AlertCategoryCount {
  /** The API's category key (`overly_granted_access`). */
  key: string;
  alertCount: number;
}

export interface DataPlatformSummary {
  /** Lowercase `AccountTypes` key — the data-source list's `dataSourceType`. */
  id: string;
  /** Display name for platforms the service catalogue has no label for. */
  name: string;
  sources: number;
  /** Distinct sources with at least one open alert — never alert events. */
  sourcesWithOpenAlerts: number;
  /** Open alert events; category counts sum to it. */
  openAlerts: number;
  categories: AlertCategoryCount[];
}

export interface DataSourceSummary {
  id: string;
  name: string;
  platformId: string;
  openAlerts: number;
  setupComplete: boolean;
  categories: AlertCategoryCount[];
}

export interface DataGovernanceSummary {
  /** When the estate was last evaluated; `null` before the first evaluation. */
  evaluatedAt: string | null;
  totalSources: number;
  monitoringPolicies: { enabled: number; total: number } | null;
  accessControls: {
    deployed: number;
    total: number;
    saved: number;
    failed: number;
  } | null;
  sourcesWithIncompleteSetup: number | null;
  platforms: DataPlatformSummary[];
  sources: DataSourceSummary[];
}

/**
 * One group × platform cell. `connected` with both counts zero is "Connected ·
 * No agents"; `not-connected` carries no counts at all.
 */
export type FleetCell =
  | { connection: "connected"; unprotected: number; protected: number }
  | { connection: "not-connected" };

/** The groupings the protection matrix can partition agents by. */
export type FleetDimension = "BUSINESS_UNIT" | "DEPARTMENT" | "GEOGRAPHY";

export interface FleetGroup {
  /** Canonical group value (`ENGINEERING`, `AMERICAS`). */
  id: string;
  /**
   * The business units whose agents make up this group - the agent list's
   * only organisational filter, so drill-downs scope through it.
   */
  businessUnits: string[];
  cells: Partial<Record<AgentPlatform, FleetCell>>;
}

export interface AgentFleetProtection {
  evaluatedAt: string | null;
  dimension: FleetDimension;
  /** Groupings the backend can partition this estate by. */
  availableDimensions: FleetDimension[];
  platforms: AgentPlatform[];
  /** Distinct agents — not a sum of overlapping group rows. */
  totalAgents: number;
  unprotected: number;
  protected: number;
  groups: FleetGroup[];
}

export interface AgentActivitySummary {
  windowHours: number;
  authorizationDecisions: number;
  policiesApplied: number;
  agentsWithIntentDrift: number;
  newAgentsImported: number;
}

const mockOnly = (): never => {
  throw new Error(
    "GovernanceOverviewService is mock-only - calls are resolved via the mock registry inside <MockScope>.",
  );
};

/*
 * Regular static METHODS, not arrow fields: the mock layer resolves entries by
 * `method.name` (see the note on `TrustAIExecutiveService`).
 */
export class GovernanceOverviewService {
  static getDataGovernanceSummaryUsingGet(_params: {
    scenario?: DataGovernanceScenario;
  }): Promise<DataGovernanceSummary> {
    return Promise.resolve(mockOnly());
  }

  static getAgentFleetProtectionUsingGet(_params: {
    dimension: FleetDimension;
    scenario?: AgentGovernanceScenario;
  }): Promise<AgentFleetProtection> {
    return Promise.resolve(mockOnly());
  }

  static getAgentActivitySummaryUsingGet(_params: {
    scenario?: AgentGovernanceScenario;
  }): Promise<AgentActivitySummary> {
    return Promise.resolve(mockOnly());
  }
}
